/*
 * "As seen on" full-size viewer (product page).
 * Click a tile -> it opens full screen. Videos play with sound and controls; photos show large.
 * ← / → (buttons or keyboard) move between tiles; ✕, Esc or clicking the dark background closes.
 * Uses the browser's built-in <dialog>, which keeps keyboard focus inside while it's open.
 */
(() => {
  if (window.asSeenOnViewer) return; // only set up once per page
  window.asSeenOnViewer = true;

  const dialog = document.createElement('dialog');
  dialog.className = 'as-seen-viewer';
  dialog.setAttribute('aria-label', 'as seen on');
  dialog.innerHTML = `
    <button type="button" class="as-seen-viewer__close" data-viewer-close aria-label="close">✕</button>
    <button type="button" class="as-seen-viewer__arrow as-seen-viewer__arrow--prev" data-viewer-step="-1" aria-label="previous">←</button>
    <figure class="as-seen-viewer__stage">
      <div class="as-seen-viewer__media" data-viewer-media></div>
      <figcaption><a class="as-seen-viewer__link" data-viewer-link target="_blank" rel="noopener" hidden>view original post</a></figcaption>
    </figure>
    <button type="button" class="as-seen-viewer__arrow as-seen-viewer__arrow--next" data-viewer-step="1" aria-label="next">→</button>
  `;
  document.body.append(dialog);

  const mediaBox = dialog.querySelector('[data-viewer-media]');
  const linkEl = dialog.querySelector('[data-viewer-link]');
  let tiles = [];
  let index = 0;

  /*
   * Shopify often hands the theme only one (big) copy of a video. Its stream list (.m3u8)
   * names every size it made, so read that and use the biggest copy no taller than maxHeight.
   * Anything goes wrong -> keep the original link.
   */
  const copies = new Map();
  const smallerCopy = (url, maxHeight) => {
    const key = `${url}|${maxHeight}`;
    if (!copies.has(key)) {
      const match = url.match(/^(.*\/)([0-9a-f]{32})\.[^/]+\.mp4/);
      copies.set(
        key,
        !match
          ? Promise.resolve(url)
          : fetch(`${match[1]}${match[2]}.m3u8`)
              .then((res) => (res.ok ? res.text() : ''))
              .then((list) => {
                const sizes = [];
                const lines = list.split('\n');
                lines.forEach((line, i) => {
                  const height = Number(line.match(/RESOLUTION=\d+x(\d+)/)?.[1]);
                  const name = lines[i + 1]?.trim().match(/^(.+?)\.((?:SD|HD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/);
                  if (height && name) sizes.push({ height, url: `${match[1]}${name[1]}.${name[2]}-${name[3]}-${name[4]}.mp4` });
                });
                if (!sizes.length) return url;
                sizes.sort((a, b) => a.height - b.height);
                return (sizes.filter((size) => size.height <= maxHeight).pop() || sizes[0]).url;
              })
              .catch(() => url)
      );
    }
    return copies.get(key);
  };

  const show = (i) => {
    index = (i + tiles.length) % tiles.length;
    const { type, src, poster, link } = tiles[index].dataset;
    mediaBox.replaceChildren();

    if (type === 'video') {
      const video = document.createElement('video');
      Object.assign(video, { controls: true, autoplay: true, loop: true, playsInline: true });
      if (poster) video.poster = poster;
      mediaBox.append(video);
      smallerCopy(src, 720).then((copy) => {
        if (!video.isConnected) return; // viewer moved on or closed meanwhile
        video.src = copy;
        video.play().catch(() => {}); // clicking counts as permission to play with sound
      });
    } else {
      const img = document.createElement('img');
      img.src = src;
      img.alt = tiles[index].querySelector('img')?.alt || '';
      mediaBox.append(img);
    }

    linkEl.hidden = !link;
    if (link) linkEl.href = link;
    dialog.querySelectorAll('[data-viewer-step]').forEach((arrow) => (arrow.hidden = tiles.length < 2));
  };

  const close = () => {
    mediaBox.replaceChildren(); // removes the video, which stops it (and its sound)
    document.body.classList.remove('drawer-open'); // let the page scroll again straight away
    if (dialog.open) dialog.close();
  };

  // Open: click any tile
  document.addEventListener('click', (event) => {
    const tile = event.target.closest('[data-as-seen-open]');
    if (!tile) return;
    tiles = [...tile.closest('.as-seen-on').querySelectorAll('[data-as-seen-open]')];
    dialog.showModal();
    document.body.classList.add('drawer-open'); // stop the page behind from scrolling
    show(tiles.indexOf(tile));
  });

  // Inside the viewer: arrows, close button, or click on the dark background
  dialog.addEventListener('click', (event) => {
    const step = event.target.closest('[data-viewer-step]');
    if (step) return show(index + Number(step.dataset.viewerStep));
    if (event.target.closest('[data-viewer-close]') || event.target === dialog) close();
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') show(index + 1);
    if (event.key === 'ArrowLeft') show(index - 1);
  });

  // Esc closes <dialog> by itself; this tidies up however it was closed
  dialog.addEventListener('close', () => {
    mediaBox.replaceChildren();
    document.body.classList.remove('drawer-open');
  });

  /*
   * Tile videos: show the still image first. Load the small video only when the tile is
   * about to scroll into view, play it silently, and pause it again when it scrolls away.
   * Phones on data saver, or set to reduce motion, just keep the still image.
   */
  const stillOnly =
    navigator.connection?.saveData || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const observer =
    !stillOnly && 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach(({ target: video, isIntersecting }) => {
              video.dataset.inView = isIntersecting ? 'true' : '';
              if (!isIntersecting) return video.pause();
              if (video.src) return video.play().catch(() => {});
              if (video.dataset.loading || !video.dataset.src) return;
              video.dataset.loading = 'true';
              smallerCopy(video.dataset.src, 480).then((copy) => {
                video.src = copy;
                if (video.dataset.inView) video.play().catch(() => {});
              });
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  const watchTiles = (root = document) => {
    if (!observer) return;
    root.querySelectorAll('video[data-as-seen-lazy]').forEach((video) => observer.observe(video));
  };

  watchTiles();
  // Theme editor re-draws the section when settings change
  document.addEventListener('shopify:section:load', (event) => watchTiles(event.target));
})();
